import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Phone, RefreshCw, ClipboardList, User, MapPin, Car, AlertCircle } from 'lucide-react';
import { TelecallerService } from '../../services/telecallerService';
import { maskPhoneLast4 } from '../../utils/phoneMask';
import { parseCallGatewayResult, shouldBlockTap, makeCoolDownState } from '../../utils/callGateway';
import CallStatusBadge from '../../components/CallStatusBadge';

const WFHDialer = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const [lead, setLead] = useState(null);
  const [callStatus, setCallStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [lastSarvResponse, setLastSarvResponse] = useState(null);
  const [callState, setCallState] = useState(null);

  const tcPhone = user?.phone || user?.contact_number || '';

  const canCall = useMemo(() => {
    const hasLead = Boolean(lead?.id && lead?.contact_number);
    const hasTcPhone = String(tcPhone || '').replace(/\D/g, '').length >= 10;
    return hasLead && hasTcPhone;
  }, [lead, tcPhone]);

  const claimNextMutation = useMutation({
    mutationFn: async () => {
      const res = await TelecallerService.claimNextWfhLead(user?.id);
      if (!res.success) throw new Error(res.error || 'Failed to claim next lead');
      return res.data;
    },
    onSuccess: (data) => {
      setLead(data || null);
      setCallStatus('');
      setNotes('');
      setLastSarvResponse(null);
      setCallState(null);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async () => {
      if (!lead?.id) return null;
      const res = await TelecallerService.releaseWfhLead(lead.id, user?.id);
      if (!res.success) throw new Error(res.error || 'Failed to release lead');
      return res.data;
    },
    onSuccess: () => {
      setLead(null);
      setCallStatus('');
      setNotes('');
      setLastSarvResponse(null);
      setCallState(null);
    },
  });

  const clickToCallMutation = useMutation({
    mutationFn: async () => {
      if (!canCall) throw new Error('Missing phone numbers');
      // Optimistic "calling" state with a 4s lock to absorb double/triple taps.
      setCallState({ status: 'calling', statusText: 'calling', reason: '', lockUntilMs: Date.now() + 4000 });
      const resp = await TelecallerService.sarvClickToCall(tcPhone, lead.contact_number, {
        lead_id: lead.id,
        telecaller_id: user?.id,
      });
      const parsed = parseCallGatewayResult(resp);
      setCallState(makeCoolDownState(parsed, parsed.ok ? 4000 : 2000));
      if (!parsed.ok) throw new Error(parsed.reason || 'Click-to-call failed');

      // Best-effort: log attempt into call_logs for reporting
      await TelecallerService.logWfhCallAttempt(
        user?.id,
        lead.id,
        `${(parsed.provider || 'sarv').toLowerCase()}_initiated`,
        null,
        resp.data
      );
      return resp.data;
    },
    onSuccess: (data) => {
      setLastSarvResponse(data);
    },
    onError: () => {
      // callState already set to "failed" inside mutationFn
    },
  });

  const saveDispositionMutation = useMutation({
    mutationFn: async () => {
      if (!lead?.id) throw new Error('No lead selected');
      if (!callStatus) throw new Error('Please select a disposition');

      const result = await TelecallerService.setWfhDisposition(user?.id, lead.id, callStatus, notes);
      if (!result.success) throw new Error(result.error || 'Failed to save disposition');

      // Best-effort call log entry (disposition result)
      await TelecallerService.logWfhCallAttempt(user?.id, lead.id, `wfh_${callStatus}`, null, null);
      return true;
    },
    onSuccess: async () => {
      // Auto-next
      await claimNextMutation.mutateAsync();
    },
  });

  const isBusy =
    claimNextMutation.isPending ||
    releaseMutation.isPending ||
    clickToCallMutation.isPending ||
    saveDispositionMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate('/telecaller/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">WFH Dialer</h1>
          <p className="text-gray-600 mt-1">Next-lead calling with click-to-call (masked company number)</p>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex flex-wrap gap-3 items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">TC:</span> {user?.name || 'Telecaller'}{' '}
          <span className="text-gray-400">•</span>{' '}
          <span className="font-medium text-gray-900">Phone:</span> {tcPhone || 'Not set'}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => claimNextMutation.mutate()}
            disabled={isBusy || !user?.id}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw size={18} />
            <span>Next Lead</span>
          </button>
          {lead?.id && (
            <button
              onClick={() => releaseMutation.mutate()}
              disabled={isBusy}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Release
            </button>
          )}
        </div>
      </div>

      {/* Error banners */}
      {(claimNextMutation.error || clickToCallMutation.error || saveDispositionMutation.error || releaseMutation.error) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
          <AlertCircle size={20} className="text-red-600 mt-0.5" />
          <div className="text-sm text-red-800">
            {String(
              claimNextMutation.error?.message ||
                clickToCallMutation.error?.message ||
                saveDispositionMutation.error?.message ||
                releaseMutation.error?.message
            )}
          </div>
        </div>
      )}

      {/* Lead card */}
      {!lead ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <User size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-700 font-medium">No lead loaded</p>
          <p className="text-gray-500 mt-1">Click “Next Lead” to claim one from your TeamLead’s WFH pool.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead details */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User size={24} className="text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{lead.name || 'Unknown'}</h2>
                  <p className="text-sm text-gray-600">{maskPhoneLast4(lead.contact_number) || 'N/A'}</p>
                </div>
              </div>
              <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                {lead.lead_source || 'WFH'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {lead.address && (
                <div className="flex items-start space-x-2">
                  <MapPin size={16} className="text-gray-400 mt-0.5" />
                  <span className="text-gray-700">{lead.address}</span>
                </div>
              )}
              {(lead.car_model || lead.car_number) && (
                <div className="flex items-start space-x-2">
                  <Car size={16} className="text-gray-400 mt-0.5" />
                  <span className="text-gray-700">
                    {[lead.car_model, lead.car_number].filter(Boolean).join(' • ')}
                  </span>
                </div>
              )}
              {(lead.pin_code || lead.state) && (
                <div className="text-gray-700">
                  <span className="font-medium">PIN/State:</span> {[lead.pin_code, lead.state].filter(Boolean).join(' / ')}
                </div>
              )}
              <div className="text-gray-700">
                <span className="font-medium">Lead ID:</span> {String(lead.id).slice(0, 8)}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              {(() => {
                const isCalling = callState?.status === 'calling' || clickToCallMutation.isPending;
                const isCoolingDown = shouldBlockTap(callState);
                const disabled = !canCall || isBusy || isCoolingDown;
                return (
                  <button
                    onClick={() => clickToCallMutation.mutate()}
                    disabled={disabled}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 text-white ${
                      disabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    <Phone size={18} className={isCalling ? 'animate-pulse' : ''} />
                    <span>{isCalling ? 'Calling...' : 'Call'}</span>
                  </button>
                );
              })()}
            </div>

            <CallStatusBadge state={callState} />

            {lastSarvResponse && (
              <details className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                <summary className="font-medium text-gray-900 cursor-pointer select-none">
                  Raw provider response
                </summary>
                <pre className="whitespace-pre-wrap break-words text-xs mt-2">
                  {JSON.stringify(lastSarvResponse, null, 2)}
                </pre>
              </details>
            )}
          </div>

          {/* Disposition */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex items-center space-x-2">
              <ClipboardList size={18} className="text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-900">Disposition</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Call Disposition *</label>
              <select
                value={callStatus}
                onChange={(e) => setCallStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                disabled={isBusy}
              >
                <option value="">Select</option>
                <option value="answered">Answered</option>
                <option value="not_answer">Not Answer</option>
                <option value="callback">Callback</option>
                <option value="not_interested">Not Interested</option>
                <option value="confirmed">Confirmed</option>
                <option value="call_cut">Call cut</option>
                <option value="car_sold">Car sold</option>
                <option value="dnd_call">DND call</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Add notes..."
                disabled={isBusy}
              />
            </div>

            <button
              onClick={() => saveDispositionMutation.mutate()}
              disabled={isBusy || !callStatus}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveDispositionMutation.isPending ? 'Saving...' : 'Save & Next'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WFHDialer;


